/* Reading mode — hover magnifier card + row highlight for the Order Register table (per-user, remembered). */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { ZoomIn } from "lucide-react";
import { API, getAuthHeader } from "@/App";

const HL = "or-read-hl";
const STYLE = `tr.${HL} > td { background-color: #fef08a !important; box-shadow: inset 0 1px 0 #eab308, inset 0 -1px 0 #eab308; }`;

const Field = ({ label, value, mono, tone }) => (
  <div className="min-w-0">
    <div className="text-[0.6em] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`break-words font-semibold ${mono ? "font-mono" : ""} ${tone || "text-slate-900"}`}>{value || "—"}</div>
  </div>
);

const Card = ({ rows, size, pos }) => {
  const r = rows[0];
  return (
    <div className="pointer-events-none fixed z-[9999] w-[min(560px,90vw)] rounded-xl border-2 border-amber-400 bg-white p-4 shadow-2xl"
      style={{ left: pos.x, top: pos.y, fontSize: size }} data-testid="or-read-card">
      <div className="mb-2 flex items-baseline justify-between gap-3 border-b pb-2">
        <span className="font-bold text-slate-900" data-testid="or-read-customer">{r.customer_name || "—"}</span>
        <span className="font-mono text-[0.8em] text-slate-500">{r.code}</span>
      </div>
      <div className="grid grid-cols-3 gap-x-4 gap-y-2">
        <Field label="Order date" value={r.date} />
        <Field label="Country" value={r.country_display} />
        <Field label="Service" value={r.mode} />
        <Field label="Tracking" value={r.tracking} mono />
        <Field label="Invoice" value={r.invoice_no ? `${r.invoice_no}${r.parcel_seq ? ` (${String(r.parcel_seq).padStart(2, "0")})` : ""}` : ""} />
        <Field label="Status" value={r.cancelled ? "Cancelled" : r.invoice_status === "completed" ? "Completed" : r.invoice_no ? "In progress" : "Pending"} />
      </div>
      {(r.reason || "").trim() && <div className="mt-2"><Field label="Reason" value={r.reason} tone="text-rose-700" /></div>}
      <table className="mt-3 w-full border-t" data-testid="or-read-products">
        <thead><tr className="text-left text-[0.6em] uppercase text-slate-400">
          <th className="py-1 pr-2">Product</th><th className="py-1 pr-2">Second name</th><th className="py-1 text-right">Qty</th></tr></thead>
        <tbody>
          {rows.map(x => (
            <tr key={x.id} className="border-t border-slate-100 align-top">
              <td className="py-1 pr-2 font-semibold text-slate-900">{x.medicine}</td>
              <td className="py-1 pr-2 text-slate-600">{x.second_name || "—"}</td>
              <td className="py-1 text-right font-bold text-slate-900">{x.quantity}{x.qty1 ? <span className="ml-1 text-[0.7em] text-pink-600">({x.qty1})</span> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const OrReadingMode = ({ rows }) => {
  const [cfg, setCfg] = useState({ on: false, size: 18 });
  const [hover, setHover] = useState(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  useEffect(() => {
    axios.get(`${API}/users/me/ui-prefs`, { headers: getAuthHeader() })
      .then(r => { const p = r.data?.ui_prefs?.or_reading_mode; if (p) setCfg({ on: !!p.on, size: p.size || 18 }); })
      .catch(() => {});
  }, []);
  const save = (next) => {
    setCfg(next);
    axios.put(`${API}/users/me/ui-prefs`, { ui_prefs: { or_reading_mode: next } }, { headers: getAuthHeader() }).catch(() => {});
  };
  useEffect(() => {
    if (!cfg.on) return undefined;
    const box = document.querySelector('[data-testid="or-table-scroll"]');
    if (!box) return undefined;
    let cur = null, raf = 0;
    const clear = () => { document.querySelectorAll(`tr.${HL}`).forEach(el => el.classList.remove(HL)); cur = null; setHover(null); };
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      const { clientX, target } = e;
      raf = requestAnimationFrame(() => {
        const tr = target.closest?.('tr[data-parcel-key]');
        if (!tr) { clear(); return; }
        const key = tr.getAttribute("data-parcel-key");
        if (key !== cur) {
          document.querySelectorAll(`tr.${HL}`).forEach(el => el.classList.remove(HL));
          box.querySelectorAll(`tr[data-parcel-key="${CSS.escape(key)}"]`).forEach(el => el.classList.add(HL));
          cur = key;
        }
        const ids = new Set([...box.querySelectorAll(`tr[data-parcel-key="${CSS.escape(key)}"]`)]
          .map(el => el.getAttribute("data-testid").replace("or-row-", "")));
        const grp = rowsRef.current.filter(x => ids.has(x.id));
        if (!grp.length) return;
        const w = Math.min(560, window.innerWidth * 0.9);
        const x = clientX + 28 + w > window.innerWidth ? Math.max(8, clientX - w - 28) : clientX + 28;
        const rect = tr.getBoundingClientRect();
        const estH = (240 + grp.length * 34) * cfg.size / 18;
        const y = rect.bottom + 8 + estH < window.innerHeight ? rect.bottom + 8 : Math.max(8, rect.top - estH - 8);
        setHover({ rows: grp, pos: { x, y } });
      });
    };
    box.addEventListener("mousemove", onMove);
    box.addEventListener("mouseleave", clear);
    box.addEventListener("scroll", clear);
    return () => { cancelAnimationFrame(raf); box.removeEventListener("mousemove", onMove); box.removeEventListener("mouseleave", clear); box.removeEventListener("scroll", clear); clear(); };
  }, [cfg.on, cfg.size]);
  const step = (d) => save({ ...cfg, size: Math.min(28, Math.max(14, cfg.size + d)) });
  return (
    <span className="inline-flex items-center gap-0.5">
      <style>{STYLE}</style>
      <button type="button" onClick={() => save({ ...cfg, on: !cfg.on })} data-testid="or-reading-mode-btn" aria-pressed={cfg.on}
        title="Reading mode — hover a row to see it in large text"
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${cfg.on
          ? "border-amber-500 bg-amber-400 text-slate-900" : "border-amber-400 bg-white text-amber-700 hover:bg-amber-50"}`}>
        <ZoomIn className="w-3.5 h-3.5" /> Reading{cfg.on ? " ON" : ""}
      </button>
      {cfg.on && (
        <>
          <button type="button" onClick={() => step(-2)} data-testid="or-reading-smaller" className="h-6 w-6 rounded border text-[11px] font-bold hover:bg-slate-50" title="Smaller">A−</button>
          <span className="w-8 text-center text-[10px] text-slate-500" data-testid="or-reading-size">{cfg.size}px</span>
          <button type="button" onClick={() => step(2)} data-testid="or-reading-bigger" className="h-6 w-6 rounded border text-sm font-bold hover:bg-slate-50" title="Bigger">A+</button>
        </>
      )}
      {cfg.on && hover && createPortal(<Card rows={hover.rows} size={cfg.size} pos={hover.pos} />, document.body)}
    </span>
  );
};
